<script lang="ts" setup>
import AuthorityBanner from '../AuthorityBanner/AuthorityBanner.vue';
import DiagnoseLink from '../DiagnoseLink.vue';
import EveryonesReviews from '../EveryonesReviews/EveryonesReviews.vue';
import FAQ, { type QAndA } from '../FAQ/FAQ.vue';
import HeroPrepareTitle from '../HeroPrepareTitle.vue';
import LpBreadcrumbs, { type Breadcrumbs } from '../LpBreadcrumbs.vue';
import MediaSection from '../MediaSection.vue';
import ProblemPresentation from '../ProblemPresentation/ProblemPresentation.vue';
import ProductLpHero from '../ProductLpHero.vue';
import ProductLpTemplateConsultProfessional from '../ProductLpTemplateConsultProfessional.vue';
import ScrollToTopButton from '../ScrollToTopButton/ScrollToTopButton.vue';
import SeminarSection from '../SeminarSection.vue';
import Steps from '../Steps/Steps.vue';
import WhereToConsult from '../WhereToConsult/WhereToConsult.vue';
import WhyFreeConsultation from '../WhyFreeConsultation/WhyFreeConsultation.vue';
import YouCanDoThis from '../YouCanDoThis/YouCanDoThis.vue';
import { commonFaqList } from '../common/commonFaqList';
import type { KvImageSrcset } from '../kvImageSrcsets';
import okanekoMagazineBanner from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/okaneko-magazine-banner.png/okaneko-magazine-banner.webp';
import {
  SeminarFragment,
  useProductLpDevKeywordsQuery,
} from '../useProductLpDevKeywordsQuery';
import ProblemPresentationCard1 from './ProblemPresentationCard1.vue';
import ProblemPresentationCard2 from './ProblemPresentationCard2.vue';
import ProblemPresentationCard3 from './ProblemPresentationCard3.vue';
import ProblemPresentationHeading from './ProblemPresentationHeading.vue';
import WhereToConsultCard1 from './WhereToConsultCard/WhereToConsultCard1.vue';
import WhereToConsultCard2 from './WhereToConsultCard/WhereToConsultCard2.vue';
import WhereToConsultCard3 from './WhereToConsultCard/WhereToConsultCard3.vue';
import WhereToConsultCard4 from './WhereToConsultCard/WhereToConsultCard4.vue';
import YouCanDoThisCard1 from './YouCanDoThisCard1.vue';
import YouCanDoThisCard2 from './YouCanDoThisCard2.vue';
import YouCanDoThisCard3 from './YouCanDoThisCard3.vue';
import kvImage380 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/marriageCost/kv_marriageCost.png/kv_marriageCost_width=380.webp';
import kvImage560 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/marriageCost/kv_marriageCost.png/kv_marriageCost_width=560.webp';
import kvImage600 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/marriageCost/kv_marriageCost.png/kv_marriageCost_width=600.webp';
import kvImage1200 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/marriageCost/kv_marriageCost.png/kv_marriageCost_width=1200.webp';
import AppFooter from '@/components/organisms/AppFooter/AppFooter.vue';
import PointsSummary from '@/components/organisms/PointsSummary/PointsSummary.vue';
import InterviewCard from '~/components/organisms/InterviewCard/InterviewCard.vue';

const keyword = '結婚資金';

const { data } = await useProductLpDevKeywordsQuery({
  keywords: ['結婚'], // 拾いたいキーワード: '結婚資金', '結婚', '結婚式'
});
const seminars = useComputedFragment(
  SeminarFragment,
  () => data.value?.filterSeminars.edges.map((elm) => elm.node) ?? []
);

const title = '結婚資金はいくら必要？｜相場・貯蓄の無料相談ならオカネコ';
const description =
  '効率よく結婚資金を貯めたい、平均費用や貯蓄額から最適な結婚資金の準備方法をプロのFPに無料相談できます。';

const dateModified = '2025-01-21';

const breadcrumbs: Breadcrumbs = [
  {
    label: 'オカネコ',
    location: '/',
    description: useConstants().WEB.description,
  },
  {
    label: `${keyword}のやり方・相談`,
    description: description,
  },
];

const kvImageSrcset: KvImageSrcset = {
  1200: kvImage1200,
  600: kvImage600,
  560: kvImage560,
  380: kvImage380,
};

const faqList: QAndA[] = [
  {
    q: 'ライフプランシミュレーションをすると何がわかるのですか？',
    a: 'ライフプランシミュレーションを行うことで、年次ごとの家計の収支が可視化され、どのタイミングでどのような支出が発生するのかがわかります。その結果、結婚資金を貯めるために何をすべきかが明確になります。また、現在の家計の問題点を把握でき、漠然としたお金の不安を解消する手助けにもなります。これにより、どのような結婚式を挙げたいか、新生活を送りたいかを具体的に考える良い機会となるでしょう。',
  },
  {
    q: '結婚資金はいつから・いくら貯めるべきですか？',
    a: 'A. 必要な貯金額は人それぞれですが、結納から新婚旅行までにかかった資金の平均は約470万円、新生活でのインテリア・家具および家電製品の購入費用の平均は約53.2万円です。ただし、具体的な必要額がわからなくても、早めに貯金を始めることが推奨されます。（「ゼクシィ結婚トレンド調査2023首都圏」「新婚生活実態調査2023（リクルートブライダル総研調べ）」より）',
  },
  ...commonFaqList,
];

const mediaProps = {
  location: 'https://okane-kenko.jp/media/',
  imageSrc: okanekoMagazineBanner,
  relatedArticleLinks: [
    {
      title:
        '結婚式費用は平均約327万円！相場から考える結婚式の資金の準備方法とは',
      location: 'https://okane-kenko.jp/media/wedding-expenses/',
    },
  ],
};

const interviewIds = ['1', '2', '3', '4'] as const;

useAppMeta({
  title: title,
  hasTitleSuffix: false,
  description: description,
});

const {
  public: { appOrigin },
} = useRuntimeConfig();
const route = useRoute();
const currentCanonicalUrl = appOrigin + route.path.replace(/\/+$/, '');
useJsonldWebPageForProductLP(
  currentCanonicalUrl,
  title,
  description,
  dateModified
);
</script>

<template>
  <div :class="$style.layout">
    <LpBreadcrumbs :breadcrumbs="breadcrumbs" />
    <ProductLpHero :class="$style.hero" :kv-image-srcset="kvImageSrcset">
      <template #heading> {{ keyword }}についてお悩みの方へ</template>
      <template #title>
        <HeroPrepareTitle :keyword="keyword" />
      </template>
      <template #description>
        {{ description }}
      </template>
    </ProductLpHero>
    <AuthorityBanner :class="$style.authority" />
    <ProblemPresentation :class="$style.problemPresentation" :keyword="keyword">
      <template #heading>
        <ProblemPresentationHeading />
      </template>
      <template #default>
        <ProblemPresentationCard1 :image-number="1" />
        <ProblemPresentationCard2 :image-number="2" />
        <ProblemPresentationCard3 :image-number="3" />
      </template>
    </ProblemPresentation>
    <WhereToConsult
      :class="$style.whereToConsult"
      :keyword="keyword"
      title="結婚資金はいくら必要？自分にあった貯め方は？"
      :date-modified="dateModified"
    >
      <WhereToConsultCard1 :image-number="0" />
      <WhereToConsultCard2 :image-number="1" />
      <WhereToConsultCard3 :image-number="2" />
      <WhereToConsultCard4 :image-number="3" />
    </WhereToConsult>
    <ProductLpTemplateConsultProfessional
      :keyword="keyword"
      :class="$style.consultProfessional"
    />
    <YouCanDoThis :keyword="keyword" :class="$style.youCanDoThis">
      <YouCanDoThisCard1 :image-number="1" />
      <YouCanDoThisCard2 :image-number="2" />
      <YouCanDoThisCard3 :image-number="3" />
    </YouCanDoThis>
    <WhyFreeConsultation :class="$style.whyFreeConsultation" />
    <PointsSummary :class="$style.pointsSummary" />
    <DiagnoseLink :class="$style.diagnoseLink1" />
    <Steps :class="$style.steps" />
    <EveryonesReviews :class="$style.everyonesReviews">
      <InterviewCard
        v-for="id in interviewIds"
        :id="id"
        :key="id"
        variant="complicated"
      />
    </EveryonesReviews>
    <FAQ :class="$style.faq" :faq-list="faqList" />
    <DiagnoseLink :class="$style.diagnoseLink2" />
    <section
      v-if="seminars.length > 0 || !!mediaProps"
      :class="$style.seminarAndMedia"
    >
      <SeminarSection
        :seminars="seminars"
        seminars-link="/seminar"
        :category="keyword"
      />
      <MediaSection :keyword="keyword" :media="mediaProps" />
      <DiagnoseLink :class="$style.diagnoseLink3" />
    </section>
    <AppFooter />
    <ScrollToTopButton :class="$style.layout__scrollToTop"></ScrollToTopButton>
  </div>
</template>

<style lang="scss" module>
@use '../_constants.scss' as *;

img {
  vertical-align: top;
}

.layout {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  @include mobile-only {
    padding-bottom: 200px;
  }
  &__scrollToTop {
    display: block;
    position: absolute;
    right: 40px;
    bottom: 40px;
    @include mobile-only {
      right: 0;
      left: 0;
      margin: auto auto;
      bottom: 64px;
    }
  }
}

.hero {
  align-self: stretch;
}

.authority {
  max-width: 520px;
  width: 100vw;
}

.problemPresentation {
  margin-top: 40px;
}

.whereToConsult {
  margin-top: 64px;
  width: 100%;

  @include mobile-only {
    margin-top: 40px;
  }
}

.researchData {
  margin-top: 64px;
  width: 100%;

  @include mobile-only {
    margin-top: 40px;
  }
}

.consultProfessional {
  margin-top: 80px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.youCanDoThis {
  margin-top: 40px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.whyFreeConsultation {
  margin-top: 40px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.pointsSummary {
  max-width: $content-width;
  padding: 16px;

  @include tablet-upper {
    padding-block: 0;
  }

  &:nth-child(n + 2) {
    margin-top: 20px;

    @include tablet-upper {
      margin-top: 40px;
    }
  }
}

.seminarAndMedia {
  width: 100%;
  margin-top: 64px;
  padding: 56px 0;
  background-color: $color-surface-accent-primary-light;
  @include mobile-only {
    padding: 32px 0;
  }
  & > * {
    margin-right: auto;
    margin-left: auto;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.steps {
  margin-top: 64px;

  @include mobile-only {
    margin-top: 48px;
  }
}

.faq {
  width: 100%;
}

.diagnoseLink1 {
  margin-top: 40px;
}
.diagnoseLink2 {
  margin-top: 40px;
}
.diagnoseLink3 {
  margin-top: 56px;
}
</style>
